/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { createPortal } from 'react-dom';

import type { ModalProps } from '../../types';
import Icon from '../Icon/Icon';
import './Modal.less';

const WIDTH_PRESETS: Record<string, number> = {
  small: 400,
  medium: 600,
  large: 800,
};

const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open = false,
      onClose,
      title,
      footer,
      width = 'medium',
      closable = true,
      maskClosable = false,
      destroyOnClose = false,
      padding = 6,
      className,
      children,
    },
    ref
  ) => {
    const panelRef = React.useRef<HTMLDivElement>(null);
    const previousFocusRef = React.useRef<HTMLElement | null>(null);

    // Defer the --open class by one frame so the browser paints the hidden
    // state first, ensuring the CSS transition fires even when destroyOnClose
    // mounts the element with open=true already set.
    const [isAnimOpen, setIsAnimOpen] = React.useState(false);
    React.useEffect(() => {
      if (!open) {
        setIsAnimOpen(false);
        return;
      }
      const id = requestAnimationFrame(() => setIsAnimOpen(true));
      return () => cancelAnimationFrame(id);
    }, [open]);

    // Body scroll lock
    React.useEffect(() => {
      if (!open) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }, [open]);

    // Escape key
    React.useEffect(() => {
      if (!open || !closable) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, closable, onClose]);

    // Focus trap
    React.useEffect(() => {
      if (!open) return;
      const activeEl = document.activeElement;
      if (activeEl instanceof HTMLElement) {
        previousFocusRef.current = activeEl;
      }
      const container = panelRef.current;
      if (!container) return;

      const getFocusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusables = getFocusables();
        if (!focusables.length) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', handleTab);
      getFocusables()[0]?.focus();

      return () => {
        document.removeEventListener('keydown', handleTab);
        previousFocusRef.current?.focus();
      };
    }, [open]);

    const hasHeader = !!(title || closable);
    const hasFooter = footer !== undefined && footer !== null;

    const setRefs = (node: HTMLDivElement | null) => {
      (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const resolvedWidth = typeof width === 'number' ? width : WIDTH_PRESETS[width] ?? WIDTH_PRESETS.medium;

    if (!open && destroyOnClose) return null;

    const content = (
      <>
        <div
          data-testid="ds-modal-mask"
          className={cx('ds-modal__mask', { 'ds-modal__mask--open': isAnimOpen })}
          onClick={maskClosable ? onClose : undefined}
        />
        <div
          ref={setRefs}
          data-testid="ds-modal"
          className={cx(
            'ds-modal__panel',
            {
              'ds-modal__panel--open': isAnimOpen,
            },
            className
          )}
          style={{ width: `${resolvedWidth}px` }}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : undefined}>
          {hasHeader && (
            <div className="ds-modal__header">
              {title && <span className="ds-modal__title">{title}</span>}
              {closable && (
                <button type="button" className="ds-modal__close" onClick={onClose} aria-label="Close modal">
                  <Icon name="actionable/close" size="small" />
                </button>
              )}
            </div>
          )}
          <div className={cx('ds-modal__body', `ds-p-${padding}`)}>{children}</div>
          {hasFooter && (
            <div data-testid="ds-modal-footer" className="ds-modal__footer">
              {footer}
            </div>
          )}
        </div>
      </>
    );

    return createPortal(content, document.body);
  }
);

Modal.displayName = 'Modal';

export default Modal;
